import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withTheme } from 'styled-components'
import { ErrorBoundary } from '@sainsburys-tech/bolt'
import { useCookieConsent, useInterval } from '@sainsburys-tech/boltui-hooks'
import { canUseDom } from '@sainsburys-tech/boltui-utils'

const GoogleDFPAds = props => {
  const { channelId, taxonomy, position, theme } = props

  const [isScriptInjected, setIsScriptInjected] = useState(false)
  const [isApiReady, setIsApiReady] = useState(false)
  const [slotFulfilled, setSlotFulfilled] = useState(false)

  const hasUserConsent = useCookieConsent('marketing')

  const containerId = `google-dfp-ad-slot--${position}`

  const hostname = {
    argos: 'argos.co.uk',
    habitat: 'habitat.co.uk'
  }[theme.tokens.channel || 'argos']

  const injectScript = () => {
    const scriptTag = document.createElement('script')
    scriptTag.async = true
    scriptTag.defer = true
    scriptTag.setAttribute('data-name', 'google-dfp')
    scriptTag.src = `//www.googletagservices.com/tag/js/gpt.js`
    document.head.appendChild(scriptTag)
    setIsScriptInjected(true)
  }

  const getSlot = (channelId, taxonomy, position) => {
    return `/${channelId}/${hostname}/${taxonomy}/${position}`
  }

  const useDfpSlot = ({ path, size, id }) => {
    const googletag = window.googletag || {}
    googletag.cmd = googletag.cmd || []

    googletag.cmd.push(() => {
      googletag.destroySlots()
      googletag.pubads().enableSingleRequest()
      googletag.pubads().setCentering(true)
      googletag.pubads().collapseEmptyDivs()
      googletag.defineSlot(path, size, id).addService(googletag.pubads())
      googletag.pubads().addEventListener('slotRenderEnded', event => {
        if (event.slot.getSlotElementId() === id) {
          setSlotFulfilled(!event.isEmpty)
        }
      })
      googletag.enableServices()
      googletag.display(id)
    })
  }

  // Inject script
  useEffect(() => {
    if (hasUserConsent && !isScriptInjected) injectScript()
  }, [hasUserConsent, isScriptInjected])

  // Poll for API-ready
  useInterval(
    () => {
      setIsApiReady(canUseDom() ? window.googletag && window.googletag.apiReady : false)
    },
    250,
    isApiReady
  )

  useEffect(() => {
    if (isApiReady) {
      useDfpSlot({
        path: getSlot(channelId, taxonomy, position),
        size: ['fluid', [870, 160], [728, 90], [345, 140]],
        id: containerId
      })
    }
  }, [isApiReady, taxonomy])

  return (
    hasUserConsent && (
      <ErrorBoundary>
        <div
          id={containerId}
          className='xs-stack md-row google-dfp'
          data-test='google-dfp'
          data-slot-fulfilled={slotFulfilled}
        />
      </ErrorBoundary>
    )
  )
}

GoogleDFPAds.propTypes = {
  channelId: PropTypes.number.isRequired,
  taxonomy: PropTypes.string.isRequired,
  position: PropTypes.string.isRequired,
  theme: PropTypes.object.isRequired
}

export default withTheme(GoogleDFPAds)
